import React from 'react';
import { motion } from 'framer-motion';
import { ValidCell } from '../types';

interface CompactPlayableCellProps {
    position: ValidCell;
    state: 'normal' | 'hover' | 'selected' | 'occupied';
    team?: 'A' | 'B';
    onClick?: () => void;
    onHover?: (hovered: boolean) => void;
    showSelectionOrb?: boolean;
}

const CompactPlayableCell: React.FC<CompactPlayableCellProps> = ({
    position,
    state,
    team,
    onClick,
    onHover,
    showSelectionOrb = false
}) => {
    const getBackgroundColor = () => {
        if (state === 'occupied') {
            return team === 'A' ? 'var(--team-a)' : 'var(--team-b)';
        }
        if (state === 'selected' || state === 'hover') {
            return team === 'A'
                ? 'rgba(59, 130, 246, 0.2)'
                : 'rgba(239, 68, 68, 0.2)';
        }
        return 'rgba(30, 41, 59, 0.5)';
    };

    const getBorderColor = () => {
        if (state === 'selected') {
            return team === 'A' ? 'var(--team-a)' : 'var(--team-b)';
        }
        if (state === 'hover') {
            return 'var(--border-color-light)';
        }
        return 'var(--border-color)';
    };

    const getGlowColor = () => {
        return team === 'A'
            ? 'rgba(59, 130, 246, 0.4)'
            : 'rgba(239, 68, 68, 0.4)';
    };

    return (
        <motion.div
            onClick={onClick}
            onMouseEnter={() => onHover?.(true)}
            onMouseLeave={() => onHover?.(false)}
            whileHover={state !== 'occupied' ? { scale: 1.05 } : {}}
            whileTap={state !== 'occupied' ? { scale: 0.95 } : {}}
            style={{
                width: '50px',
                height: '50px',
                position: 'relative',
                cursor: state !== 'occupied' ? 'pointer' : 'default',
                userSelect: 'none'
            }}
        >
            {/* Main Cell Body */}
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    background: getBackgroundColor(),
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: state === 'hover' || state === 'selected'
                        ? `0 0 15px ${getGlowColor()}, 0 4px 8px rgba(0,0,0,0.3)`
                        : '0 2px 6px rgba(0,0,0,0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease'
                }}
            >
                {/* Label on "Roof" */}
                <div
                    style={{
                        position: 'absolute',
                        top: '2px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.65rem',
                        fontWeight: '700',
                        fontFamily: 'monospace',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.5)'
                    }}
                >
                    {position}
                </div>

                {/* Selection Orb */}
                {showSelectionOrb && state === 'selected' && (
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                        style={{
                            position: 'absolute',
                            bottom: '8px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: team === 'A'
                                ? 'linear-gradient(135deg, #3B82F6, #60A5FA)'
                                : 'linear-gradient(135deg, #EF4444, #F87171)',
                            boxShadow: `0 0 15px ${team === 'A' ? 'rgba(59, 130, 246, 0.8)' : 'rgba(239, 68, 68, 0.8)'}`,
                            border: '2px solid white'
                        }}
                    >
                        <motion.div
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [1, 0.7, 1]
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: 'easeInOut'
                            }}
                            style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                background: 'inherit'
                            }}
                        />
                    </motion.div>
                )}

                {/* 3D Shadow Effect */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: '-3px',
                        left: '5%',
                        right: '5%',
                        height: '3px',
                        background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.4), transparent)',
                        borderRadius: '50%',
                        filter: 'blur(2px)'
                    }}
                />
            </div>

            {/* Pulse Animation for Selected State */}
            {state === 'selected' && (
                <motion.div
                    animate={{
                        scale: [1, 1.15, 1],
                        opacity: [0.5, 0.2, 0.5]
                    }}
                    transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                    }}
                    style={{
                        position: 'absolute',
                        top: '-3px',
                        left: '-3px',
                        right: '-3px',
                        bottom: '-3px',
                        border: `2px solid ${team === 'A' ? 'var(--team-a)' : 'var(--team-b)'}`,
                        borderRadius: 'var(--radius-sm)',
                        pointerEvents: 'none'
                    }}
                />
            )}
        </motion.div>
    );
};

export default CompactPlayableCell;
